import { useCallback, useMemo, useState } from 'react'
import type { ListFilter, ListSort } from '../types/scheduleList.types'

// 리스트 보기 화면 상태 (구현계획 C-02) — 필터·정렬·펼침 집합·팝업 열림.
//  - 필터(기본 전체)·정렬(기본 일정순)은 현장을 바꿔도 유지한다(신본 코드 동작, 계획서 6-12). 화면을 벗어나면 저장하지 않는다(useState 만 사용).
//  - 펼침(taskId 기준, 여러 행 동시)과 열린 팝업은 현장을 바꾸면 초기화한다. 값에 현장 id 를 함께 담아 비교하므로
//    effect 없이 같은 렌더에서 비워진다(한 프레임 이전 현장의 펼침이 보이지 않음).
//  - 현장 전환에서 컴포넌트가 다시 마운트되지 않도록 호출 쪽이 key={scheduleId} 를 쓰지 않는다(그러면 필터·정렬이 지워진다).

/** 리스트가 직접 호스팅하는 팝업(D-10). focusTaskId = 열릴 때 먼저 고를 공종 */
export type ListPopup =
  | { kind: 'sigong' }
  | { kind: 'material'; focusTaskId: number | null }
  | { kind: 'ship'; focusTaskId: number | null }
  | { kind: 'status' }
  | { kind: 'addTask' }

const EMPTY_EXPANDED: ReadonlySet<number> = new Set<number>()

interface ScopedExpanded { scheduleId: number | null; ids: ReadonlySet<number> }
interface ScopedPopup { scheduleId: number | null; popup: ListPopup | null }

export function useScheduleListState(scheduleId: number | null | undefined) {
  const sid = scheduleId ?? null
  const [filter, setFilter] = useState<ListFilter>('')
  const [sort, setSort] = useState<ListSort>('date')
  const [expandedState, setExpandedState] = useState<ScopedExpanded>({ scheduleId: sid, ids: EMPTY_EXPANDED })
  const [popupState, setPopupState] = useState<ScopedPopup>({ scheduleId: sid, popup: null })

  // 다른 현장의 값이면 비어 있는 것으로 읽는다
  const expanded = expandedState.scheduleId === sid ? expandedState.ids : EMPTY_EXPANDED
  const popup = popupState.scheduleId === sid ? popupState.popup : null

  /** 정렬 버튼 — 누르면 반대로 바뀜 */
  const toggleSort = useCallback(() => setSort(s => (s === 'date' ? 'urgent' : 'date')), [])

  /** 행 클릭 — 해당 행만 펼침/접힘(여러 행 동시 가능). 필터·정렬을 바꿔도 펼침은 taskId 로 유지된다 */
  const toggleExpanded = useCallback((taskId: number) => {
    setExpandedState(prev => {
      const base = prev.scheduleId === sid ? prev.ids : EMPTY_EXPANDED
      const next = new Set(base)
      if (next.has(taskId)) next.delete(taskId)
      else next.add(taskId)
      return { scheduleId: sid, ids: next }
    })
  }, [sid])

  const isExpanded = useCallback((taskId: number) => expanded.has(taskId), [expanded])

  const openPopup = useCallback((p: ListPopup) => setPopupState({ scheduleId: sid, popup: p }), [sid])
  const closePopup = useCallback(() => setPopupState({ scheduleId: sid, popup: null }), [sid])

  return useMemo(() => ({
    filter, setFilter,
    sort, setSort, toggleSort,
    expanded, isExpanded, toggleExpanded,
    popup, openPopup, closePopup,
  }), [filter, sort, toggleSort, expanded, isExpanded, toggleExpanded, popup, openPopup, closePopup])
}

export type ScheduleListState = ReturnType<typeof useScheduleListState>
